/**
 * Reconciliación de Agencias desde CSV Oficial
 *
 * Sube el CSV oficial de miembros AMPI (con columna AGENCIA),
 * matchea agencias contra la colección inmobiliaria en Firestore,
 * permite crear faltantes, y actualiza la asignación de cada miembro.
 */

import React, { useState, useCallback } from 'react';
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import { normalize, matchAgencia, AgenciaItem, calculateSimilarity } from '../../utils/matching';
import { MatchResult } from '../../types/entities';
import { propagarMiembroOficial } from '../../utils/propagarMiembroOficial';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

// ============ INTERFACES ============

interface CsvRowWithAgency {
  nombre: string;
  correo: string;
  telefono: string;
  tipo: 'ASOCIADO' | 'AFILIADO' | '';
  agencia: string;
}

interface InmobiliariaDoc {
  id: string;
  nombre: string;
}

interface MiembroDoc {
  docId: string;
  collection: 'asesor' | 'asociado';
  nombre: string;
  correo: string;
  telefono?: string;
  inmobiliaria_doc_id?: string;
  inmobiliaria_doc_ids?: string[];
}

interface AgencyMatchEntry {
  csvAgencyName: string;
  normalizedName: string;
  memberCount: number;
  matchResult: MatchResult;
  confirmedInmobiliariaId: string | null;
  createNew: boolean;
  createdDocId?: string;
}

interface MemberAssignment {
  csvRow: CsvRowWithAgency;
  member: MiembroDoc | null;
  matchConfidence: number;
  expectedInmobiliariaId: string;
  expectedAgencyName: string;
  currentInmobiliariaId: string;
  currentAgencyName: string;
  needsUpdate: boolean;
  needsPhoneUpdate: boolean;
  currentPhone: string;
}

type Step = 'upload' | 'agency-review' | 'member-review' | 'applying' | 'done';

/** Normaliza teléfono para comparación:
 *  - Quita todo excepto dígitos
 *  - Quita prefijo internacional de México (52) si tiene 12+ dígitos
 *  - Quita el 1 intercalado de larga distancia antigua (ej: 52 1 322...)
 *  Resultado: 10 dígitos (lada + número local) */
function normPhone(tel: string): string {
  let digits = (tel || '').replace(/[^0-9]/g, '');
  // Quitar prefijo 52 (México) → quedan 10 dígitos
  if (digits.length === 12 && digits.startsWith('52')) {
    digits = digits.slice(2);
  }
  // Quitar prefijo 521 (México con 1 intercalado) → quedan 10 dígitos
  if (digits.length === 13 && digits.startsWith('521')) {
    digits = digits.slice(3);
  }
  // Quitar 1 al inicio si quedaron 11 dígitos (larga distancia nacional)
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits;
}

// ============ COMPONENT ============

export default function ReconciliarAgenciasCsv() {
  const { user } = useUser();

  const [step, setStep] = useState<Step>('upload');
  const [csvRows, setCsvRows] = useState<CsvRowWithAgency[]>([]);
  const [inmobiliarias, setInmobiliarias] = useState<InmobiliariaDoc[]>([]);
  const [, setMiembros] = useState<MiembroDoc[]>([]);
  const [agencyMatches, setAgencyMatches] = useState<AgencyMatchEntry[]>([]);
  const [memberAssignments, setMemberAssignments] = useState<MemberAssignment[]>([]);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<string[]>([]);
  const [filterAgency, setFilterAgency] = useState<'all' | 'exact' | 'fuzzy' | 'no_match'>('all');
  const [filterMember, setFilterMember] = useState<'all' | 'needs_update' | 'correct' | 'not_found' | 'phone_update'>('all');
  const [searchAgency, setSearchAgency] = useState('');
  const [searchMember, setSearchMember] = useState('');

  // Pagination
  const [agencyPage, setAgencyPage] = useState(1);
  const [memberPage, setMemberPage] = useState(1);
  const perPage = 25;

  const addProgress = (msg: string) => {
    setProgress(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  // ============ STEP 1: CSV PARSING ============

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split(/\r?\n/).filter(l => l.trim());

      if (lines.length < 2) {
        alert('El CSV debe tener al menos un encabezado y una fila de datos');
        return;
      }

      const header = lines[0];
      const sep = header.includes('\t') ? '\t' : header.includes(';') ? ';' : ',';
      const headers = header.split(sep).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));

      const iNombre = headers.findIndex(h => h.includes('nombre'));
      const iCorreo = headers.findIndex(h => h.includes('correo') || h.includes('email') || h.includes('mail'));
      const iTelefono = headers.findIndex(h => h.includes('telefono') || h.includes('tel') || h.includes('phone') || h.includes('numero'));
      const iTipo = headers.findIndex(h => h.includes('tipo'));
      const iAgencia = headers.findIndex(h => h.includes('agencia') || h.includes('inmobiliaria'));

      if (iNombre === -1 && iCorreo === -1) {
        alert('No se encontraron columnas NOMBRE o CORREO en el CSV');
        return;
      }
      if (iAgencia === -1) {
        alert('No se encontró columna AGENCIA o INMOBILIARIA en el CSV');
        return;
      }

      const rows: CsvRowWithAgency[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(sep).map(v => v.trim().replace(/^["']|["']$/g, ''));
        const nombre = iNombre >= 0 ? values[iNombre] || '' : '';
        const correo = iCorreo >= 0 ? values[iCorreo] || '' : '';
        const telefono = iTelefono >= 0 ? values[iTelefono] || '' : '';
        const tipoRaw = iTipo >= 0 ? (values[iTipo] || '').toUpperCase().trim() : '';
        const tipo = (tipoRaw === 'ASOCIADO' || tipoRaw === 'AFILIADO') ? tipoRaw : '' as const;
        const agencia = iAgencia >= 0 ? values[iAgencia] || '' : '';

        if ((nombre || correo) && tipo && agencia) {
          rows.push({ nombre, correo, telefono, tipo, agencia });
        }
      }

      setCsvRows(rows);
      addProgress(`CSV cargado: ${rows.length} miembros con agencia`);
    };
    reader.readAsText(file, 'UTF-8');
  }, []);

  // ============ STEP 2: AGENCY MATCHING ============

  const ejecutarMatchingAgencias = useCallback(async () => {
    if (csvRows.length === 0) return;
    setLoading(true);
    addProgress('Cargando agencias de Firestore...');

    try {
      // Load inmobiliarias
      const inmSnap = await getDocs(collection(db, 'inmobiliaria'));
      const inmDocs: InmobiliariaDoc[] = inmSnap.docs.map(d => ({
        id: d.id,
        nombre: d.data().nombre || '',
      }));
      setInmobiliarias(inmDocs);
      addProgress(`${inmDocs.length} agencias en Firestore`);

      // Build AgenciaItem[] for matchAgencia()
      const agenciaItems: AgenciaItem[] = inmDocs.map(d => ({
        inmobiliaria_id: d.id,
        nombre: d.nombre,
      }));

      // Deduplicate CSV agencies by normalized name
      const agencyGroups = new Map<string, { canonical: string; count: number }>();
      csvRows.forEach(row => {
        const norm = normalize(row.agencia);
        if (!norm) return;
        const existing = agencyGroups.get(norm);
        if (existing) {
          existing.count++;
        } else {
          agencyGroups.set(norm, { canonical: row.agencia, count: 1 });
        }
      });

      addProgress(`${agencyGroups.size} agencias únicas en CSV (de ${csvRows.length} miembros)`);

      // Match each unique agency
      const matches: AgencyMatchEntry[] = [];
      agencyGroups.forEach((group, normName) => {
        const result = matchAgencia(group.canonical, agenciaItems);
        matches.push({
          csvAgencyName: group.canonical,
          normalizedName: normName,
          memberCount: group.count,
          matchResult: result,
          confirmedInmobiliariaId: result.found && result.confidence >= 80 ? (result.id || null) : null,
          createNew: false,
        });
      });

      // Sort: no_match first, then fuzzy, then exact
      matches.sort((a, b) => {
        const prioA = !a.matchResult.found ? 0 : a.matchResult.confidence < 80 ? 1 : 2;
        const prioB = !b.matchResult.found ? 0 : b.matchResult.confidence < 80 ? 1 : 2;
        if (prioA !== prioB) return prioA - prioB;
        return a.csvAgencyName.localeCompare(b.csvAgencyName);
      });

      setAgencyMatches(matches);

      const exact = matches.filter(m => m.matchResult.found && m.matchResult.confidence >= 80).length;
      const fuzzy = matches.filter(m => m.matchResult.found && m.matchResult.confidence >= 70 && m.matchResult.confidence < 80).length;
      const noMatch = matches.filter(m => !m.matchResult.found).length;
      addProgress(`Resultados: ${exact} exactas, ${fuzzy} fuzzy, ${noMatch} sin match`);

      setStep('agency-review');
    } catch (err: any) {
      addProgress(`ERROR: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [csvRows]);

  // ============ AGENCY ACTIONS ============

  const confirmAgency = (index: number, inmobiliariaId: string) => {
    setAgencyMatches(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], confirmedInmobiliariaId: inmobiliariaId, createNew: false };
      return updated;
    });
  };

  const markCreateNew = (index: number) => {
    setAgencyMatches(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], createNew: true, confirmedInmobiliariaId: null };
      return updated;
    });
  };

  const confirmAllExact = () => {
    setAgencyMatches(prev => prev.map(m => {
      if (m.matchResult.found && m.matchResult.confidence >= 80 && m.matchResult.id) {
        return { ...m, confirmedInmobiliariaId: m.matchResult.id };
      }
      return m;
    }));
  };

  const createAllMissing = () => {
    setAgencyMatches(prev => prev.map(m => {
      if (!m.matchResult.found && !m.confirmedInmobiliariaId) {
        return { ...m, createNew: true };
      }
      return m;
    }));
  };

  // ============ CREATE MISSING AGENCIES & PROCEED TO MEMBERS ============

  const proceedToMembers = useCallback(async () => {
    setLoading(true);

    try {
      // Create missing agencies
      const toCreate = agencyMatches.filter(m => m.createNew && !m.createdDocId);
      if (toCreate.length > 0) {
        addProgress(`Creando ${toCreate.length} agencias nuevas...`);
        const batch = writeBatch(db);
        const newRefs: { index: number; ref: any }[] = [];

        toCreate.forEach(entry => {
          const globalIndex = agencyMatches.indexOf(entry);
          const ref = doc(collection(db, 'inmobiliaria'));
          batch.set(ref, {
            nombre: entry.csvAgencyName.trim(),
            descripcion: '',
            telefono: '',
            correo: '',
            direccion: '',
            años_experiencia: 0,
            redes: { facebook: '', instagram: '' },
            activo: true,
            creadoDesde: 'reconciliacion-agencias-csv',
            createdAt: serverTimestamp(),
          });
          newRefs.push({ index: globalIndex, ref });
        });

        await batch.commit();
        addProgress(`${toCreate.length} agencias creadas`);

        // Update matches with created doc IDs
        setAgencyMatches(prev => {
          const updated = [...prev];
          newRefs.forEach(({ index, ref }) => {
            updated[index] = {
              ...updated[index],
              createdDocId: ref.id,
              confirmedInmobiliariaId: ref.id,
            };
          });
          return updated;
        });

        // Also update inmobiliarias list
        const newInms = newRefs.map(({ ref }, i) => ({
          id: ref.id,
          nombre: toCreate[i].csvAgencyName.trim(),
        }));
        setInmobiliarias(prev => [...prev, ...newInms]);
      }

      // Load members from Firestore
      addProgress('Cargando miembros de Firestore...');
      const [asesorSnap, asociadoSnap] = await Promise.all([
        getDocs(collection(db, 'asesor')),
        getDocs(collection(db, 'asociado')),
      ]);

      const allMiembros: MiembroDoc[] = [];
      asesorSnap.forEach(d => {
        const data = d.data();
        allMiembros.push({
          docId: d.id,
          collection: 'asesor',
          nombre: data.nombre || '',
          correo: data.correo || '',
          telefono: data.telefono || '',
          inmobiliaria_doc_ids: Array.isArray(data.inmobiliaria_doc_ids) ? data.inmobiliaria_doc_ids : [],
        });
      });
      asociadoSnap.forEach(d => {
        const data = d.data();
        allMiembros.push({
          docId: d.id,
          collection: 'asociado',
          nombre: data.nombre || '',
          correo: data.correo || '',
          telefono: data.telefono || '',
          inmobiliaria_doc_id: data.inmobiliaria_doc_id || '',
        });
      });
      setMiembros(allMiembros);
      addProgress(`${allMiembros.length} miembros en Firestore (${asesorSnap.size} asesores, ${asociadoSnap.size} asociados)`);

      // Build inmobiliaria name map for display
      // Use the latest agencyMatches (with created IDs)
      const updatedMatches = agencyMatches.map(m => {
        if (m.createNew && !m.createdDocId) {
          const created = toCreate.find(t => t.normalizedName === m.normalizedName);
          if (created?.createdDocId) {
            return { ...m, createdDocId: created.createdDocId, confirmedInmobiliariaId: created.createdDocId };
          }
        }
        return m;
      });

      // Reload inmobiliarias for name lookup
      const inmSnap = await getDocs(collection(db, 'inmobiliaria'));
      const inmNameMap = new Map<string, string>();
      inmSnap.docs.forEach(d => inmNameMap.set(d.id, d.data().nombre || ''));

      // Build agency lookup: normalizedCsvName → confirmedInmobiliariaId
      const agencyLookup = new Map<string, string>();
      updatedMatches.forEach(m => {
        const id = m.createdDocId || m.confirmedInmobiliariaId;
        if (id) agencyLookup.set(m.normalizedName, id);
      });

      // Match CSV members to Firestore members
      const assignments: MemberAssignment[] = [];
      csvRows.forEach(row => {
        const normCorreo = normalize(row.correo);
        const normAgencia = normalize(row.agencia);

        // Find member by email first, then by name
        let bestMember: MiembroDoc | null = null;
        let bestConf = 0;

        for (const m of allMiembros) {
          if (normCorreo && normalize(m.correo) === normCorreo) {
            bestMember = m;
            bestConf = 100;
            break;
          }
        }

        if (!bestMember) {
          for (const m of allMiembros) {
            const sim = calculateSimilarity(row.nombre, m.nombre);
            if (sim > bestConf && sim >= 70) {
              bestConf = sim;
              bestMember = m;
            }
          }
        }

        // Expected agency
        const expectedId = agencyLookup.get(normAgencia) || '';
        const expectedName = expectedId ? (inmNameMap.get(expectedId) || row.agencia) : row.agencia;

        // Current agency
        let currentId = '';
        if (bestMember) {
          if (bestMember.collection === 'asesor') {
            currentId = bestMember.inmobiliaria_doc_ids?.[0] || '';
          } else {
            currentId = bestMember.inmobiliaria_doc_id || '';
          }
        }
        const currentName = currentId ? (inmNameMap.get(currentId) || '') : '';

        // Phone comparison
        const csvPhone = normPhone(row.telefono);
        const currentPhone = bestMember ? normPhone(bestMember.telefono || '') : '';
        const needsPhoneUpdate = !!bestMember && !!csvPhone && csvPhone !== currentPhone;

        const needsAgencyUpdate = !!bestMember && !!expectedId && currentId !== expectedId;

        assignments.push({
          csvRow: row,
          member: bestMember,
          matchConfidence: bestConf,
          expectedInmobiliariaId: expectedId,
          expectedAgencyName: expectedName,
          currentInmobiliariaId: currentId,
          currentAgencyName: currentName,
          needsUpdate: needsAgencyUpdate || needsPhoneUpdate,
          needsPhoneUpdate,
          currentPhone: bestMember?.telefono || '',
        });
      });

      setMemberAssignments(assignments);

      const correct = assignments.filter(a => a.member && !a.needsUpdate && a.expectedInmobiliariaId).length;
      const needsUpd = assignments.filter(a => a.needsUpdate).length;
      const notFound = assignments.filter(a => !a.member).length;
      const noAgencyResolved = assignments.filter(a => !a.expectedInmobiliariaId).length;
      const phoneUpdates = assignments.filter(a => a.needsPhoneUpdate).length;
      addProgress(`Miembros: ${correct} correctos, ${needsUpd} necesitan update (${phoneUpdates} teléfonos), ${notFound} no encontrados, ${noAgencyResolved} sin agencia resuelta`);

      setStep('member-review');
    } catch (err: any) {
      addProgress(`ERROR: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [agencyMatches, csvRows]);

  // ============ APPLY MEMBER UPDATES ============

  const aplicarCambios = useCallback(async () => {
    const toUpdate = memberAssignments.filter(a => a.needsUpdate && a.member);
    if (toUpdate.length === 0) {
      addProgress('No hay cambios que aplicar');
      return;
    }

    setStep('applying');
    setLoading(true);
    addProgress(`Aplicando ${toUpdate.length} cambios...`);

    try {
      let batchCount = 0;
      let batch = writeBatch(db);
      let opsInBatch = 0;

      for (const a of toUpdate) {
        if (!a.member) continue;
        const ref = doc(db, a.member.collection, a.member.docId);

        const updates: any = {};

        // Agency update
        const needsAgency = !!a.expectedInmobiliariaId && a.currentInmobiliariaId !== a.expectedInmobiliariaId;
        if (needsAgency) {
          if (a.member.collection === 'asesor') {
            updates.inmobiliaria_doc_ids = [a.expectedInmobiliariaId];
          } else {
            updates.inmobiliaria_doc_id = a.expectedInmobiliariaId;
          }
        }

        // Phone update — normalizar a +52 + 10 dígitos
        if (a.needsPhoneUpdate && a.csvRow.telefono) {
          const digits = normPhone(a.csvRow.telefono);
          updates.telefono = digits.length === 10 ? `+52${digits}` : a.csvRow.telefono.trim();
        }

        if (Object.keys(updates).length === 0) continue;
        batch.update(ref, updates);
        opsInBatch++;

        if (opsInBatch >= 499) {
          await batch.commit();
          batchCount++;
          addProgress(`Batch ${batchCount} completado (${opsInBatch} ops)`);
          batch = writeBatch(db);
          opsInBatch = 0;
        }
      }

      if (opsInBatch > 0) {
        await batch.commit();
        batchCount++;
        addProgress(`Batch ${batchCount} completado (${opsInBatch} ops)`);
      }

      addProgress(`Listo: ${toUpdate.length} miembros actualizados en ${batchCount} batch(es)`);

      // Propagar miembro_oficial a propiedades e inmobiliarias
      addProgress('Propagando tiene_miembros_oficiales a inmobiliarias...');
      const propResult = await propagarMiembroOficial((msg) => addProgress(msg));
      addProgress(`Propagación: ${propResult.inmobiliariasOficiales} inmobiliarias oficiales, ${propResult.marcadasOficial} props oficiales`);

      setStep('done');
    } catch (err: any) {
      addProgress(`ERROR: ${err.message}`);
      setStep('member-review');
    } finally {
      setLoading(false);
    }
  }, [memberAssignments]);

  // ============ STATS ============

  const agencyStats = {
    total: agencyMatches.length,
    exact: agencyMatches.filter(m => m.matchResult.found && m.matchResult.confidence >= 80).length,
    fuzzy: agencyMatches.filter(m => m.matchResult.found && m.matchResult.confidence >= 70 && m.matchResult.confidence < 80).length,
    noMatch: agencyMatches.filter(m => !m.matchResult.found).length,
    resolved: agencyMatches.filter(m => m.confirmedInmobiliariaId || m.createNew).length,
  };

  const memberStats = {
    total: memberAssignments.length,
    correct: memberAssignments.filter(a => a.member && !a.needsUpdate && a.expectedInmobiliariaId).length,
    needsUpdate: memberAssignments.filter(a => a.needsUpdate).length,
    notFound: memberAssignments.filter(a => !a.member).length,
    noAgency: memberAssignments.filter(a => !a.expectedInmobiliariaId).length,
    phoneUpdates: memberAssignments.filter(a => a.needsPhoneUpdate).length,
  };

  // ============ FILTERED & PAGINATED LISTS ============

  const filteredAgencies = agencyMatches.filter(m => {
    if (searchAgency) {
      const s = searchAgency.toLowerCase();
      if (!m.csvAgencyName.toLowerCase().includes(s) && !(m.matchResult.matched || '').toLowerCase().includes(s)) return false;
    }
    if (filterAgency === 'exact') return m.matchResult.found && m.matchResult.confidence >= 80;
    if (filterAgency === 'fuzzy') return m.matchResult.found && m.matchResult.confidence >= 70 && m.matchResult.confidence < 80;
    if (filterAgency === 'no_match') return !m.matchResult.found;
    return true;
  });

  const agencyTotalPages = Math.ceil(filteredAgencies.length / perPage);
  const pagedAgencies = filteredAgencies.slice((agencyPage - 1) * perPage, agencyPage * perPage);

  const filteredMembers = memberAssignments.filter(a => {
    if (searchMember) {
      const s = searchMember.toLowerCase();
      if (!a.csvRow.nombre.toLowerCase().includes(s) && !a.csvRow.correo.toLowerCase().includes(s) && !a.csvRow.agencia.toLowerCase().includes(s)) return false;
    }
    if (filterMember === 'needs_update') return a.needsUpdate;
    if (filterMember === 'phone_update') return a.needsPhoneUpdate;
    if (filterMember === 'correct') return a.member && !a.needsUpdate && !!a.expectedInmobiliariaId;
    if (filterMember === 'not_found') return !a.member;
    return true;
  });

  const memberTotalPages = Math.ceil(filteredMembers.length / perPage);
  const pagedMembers = filteredMembers.slice((memberPage - 1) * perPage, memberPage * perPage);

  // ============ RENDER ============

  if (!user || !user.tipo?.includes('admin')) {
    return <div className="container mt-5"><h2>No tienes permiso para acceder a esta sección</h2></div>;
  }

  return (
    <>
      <UserNav nombreUsuario={user?.nombre} nombreInmobiliaria={user?.inmobiliaria_nombre} />
      <Breadcrumb
        items={[
          { label: 'Inicio', path: '/' },
          { label: 'Admin', path: '/admin-dashboard' },
          { label: 'Reconciliar Agencias' },
        ]}
      />

      <section className="py-4">
        <div className="container">
          <h3><i className="fas fa-building me-2"></i>Reconciliación de Agencias desde CSV</h3>
          <p className="text-muted">
            Valida que cada miembro oficial tenga la agencia correcta según el CSV oficial.
          </p>

          {/* Steps indicator */}
          <div className="d-flex gap-2 mb-4">
            {['upload', 'agency-review', 'member-review', 'done'].map((s, i) => {
              const labels = ['1. Subir CSV', '2. Agencias', '3. Miembros', '4. Listo'];
              const isCurrent = step === s || (step === 'applying' && s === 'member-review');
              const isPast = ['upload', 'agency-review', 'member-review', 'applying', 'done'].indexOf(step) >
                ['upload', 'agency-review', 'member-review', 'applying', 'done'].indexOf(s);
              return (
                <span
                  key={s}
                  className={`badge ${isCurrent ? 'bg-primary' : isPast ? 'bg-success' : 'bg-secondary'}`}
                  style={{ fontSize: '0.85rem', padding: '8px 14px' }}
                >
                  {labels[i]}
                </span>
              );
            })}
          </div>

          {/* ==================== STEP: UPLOAD ==================== */}
          {step === 'upload' && (
            <div className="card">
              <div className="card-body">
                <h5>Subir CSV con columna AGENCIA</h5>
                <p className="text-muted small">
                  El CSV debe tener columnas: NOMBRE, CORREO, NUMERO, AGENCIA, TIPO
                </p>
                <input
                  type="file"
                  accept=".csv,.txt,.tsv"
                  className="form-control mb-3"
                  onChange={handleFileUpload}
                />

                {csvRows.length > 0 && (
                  <>
                    <div className="alert alert-info">
                      <strong>{csvRows.length}</strong> miembros cargados del CSV
                    </div>

                    <div className="table-responsive" style={{ maxHeight: 400 }}>
                      <table aria-label="Tabla de Reconciliar Agencias Csv" className="table table-sm table-striped">
                        <thead className="table-dark">
                          <tr>
                            <th scope="col">#</th>
                            <th scope="col">Nombre</th>
                            <th scope="col">Correo</th>
                            <th scope="col">Tipo</th>
                            <th scope="col">Agencia (CSV)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvRows.slice(0, 20).map((row, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{row.nombre}</td>
                              <td><small>{row.correo}</small></td>
                              <td><span className={`badge ${row.tipo === 'ASOCIADO' ? 'bg-success' : 'bg-primary'}`}>{row.tipo}</span></td>
                              <td>{row.agencia}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {csvRows.length > 20 && <small className="text-muted">Mostrando 20 de {csvRows.length}...</small>}

                    <button
                      className="btn btn-primary mt-3"
                      onClick={ejecutarMatchingAgencias}
                      disabled={loading}
                    >
                      {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Analizando...</> : 'Analizar Agencias'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ==================== STEP: AGENCY REVIEW ==================== */}
          {step === 'agency-review' && (
            <div className="card">
              <div className="card-body">
                <h5>Validación de Agencias</h5>

                {/* Stats */}
                <div className="row g-2 mb-3">
                  <div className="col-3">
                    <div className="card border-0 bg-light text-center p-2">
                      <h4 className="mb-0">{agencyStats.total}</h4>
                      <small className="text-muted">Total</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border-0 text-center p-2" style={{ background: '#d4edda' }}>
                      <h4 className="mb-0 text-success">{agencyStats.exact}</h4>
                      <small>Exactas</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border-0 text-center p-2" style={{ background: '#fff3cd' }}>
                      <h4 className="mb-0 text-warning">{agencyStats.fuzzy}</h4>
                      <small>Fuzzy</small>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border-0 text-center p-2" style={{ background: '#f8d7da' }}>
                      <h4 className="mb-0 text-danger">{agencyStats.noMatch}</h4>
                      <small>Sin match</small>
                    </div>
                  </div>
                </div>

                {/* Resolved counter */}
                <div className={`alert ${agencyStats.resolved === agencyStats.total ? 'alert-success' : 'alert-warning'} py-2 mb-3`}>
                  <strong>{agencyStats.resolved}/{agencyStats.total}</strong> agencias resueltas
                  {agencyStats.resolved < agencyStats.total && (
                    <span> — faltan <strong>{agencyStats.total - agencyStats.resolved}</strong> por resolver</span>
                  )}
                  {agencyStats.resolved === agencyStats.total && (
                    <span> — <i className="fas fa-check me-1"></i>Todas resueltas, puedes proceder</span>
                  )}
                </div>

                {/* Bulk actions */}
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  <button className="btn btn-success btn-sm" onClick={confirmAllExact}>
                    <i className="fas fa-check-double me-1"></i>Confirmar todas las exactas
                  </button>
                  <button className="btn btn-warning btn-sm" onClick={createAllMissing}>
                    <i className="fas fa-plus me-1"></i>Marcar crear todas sin match
                  </button>
                </div>

                {/* Filters */}
                <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                  <div className="btn-group btn-group-sm">
                    {[
                      { key: 'all', label: `Todas (${agencyStats.total})` },
                      { key: 'exact', label: `Exactas (${agencyStats.exact})` },
                      { key: 'fuzzy', label: `Fuzzy (${agencyStats.fuzzy})` },
                      { key: 'no_match', label: `Sin match (${agencyStats.noMatch})` },
                    ].map(f => (
                      <button
                        key={f.key}
                        className={`btn ${filterAgency === f.key ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => { setFilterAgency(f.key as any); setAgencyPage(1); }}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ maxWidth: 250 }}
                    placeholder="Buscar agencia..."
                    value={searchAgency}
                    onChange={e => { setSearchAgency(e.target.value); setAgencyPage(1); }}
                  />
                </div>

                {/* Agency table */}
                <div className="table-responsive">
                  <table aria-label="Tabla de Reconciliar Agencias Csv" className="table table-sm table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Agencia CSV</th>
                        <th scope="col">Match Firestore</th>
                        <th scope="col">Conf.</th>
                        <th scope="col">Método</th>
                        <th scope="col">Miembros</th>
                        <th scope="col">Estado</th>
                        <th scope="col">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedAgencies.map((entry, i) => {
                        const globalIdx = agencyMatches.indexOf(entry);
                        const isResolved = !!entry.confirmedInmobiliariaId || entry.createNew;
                        const rowColor = entry.createNew ? '#fff3cd' :
                          entry.confirmedInmobiliariaId ? '#d4edda' :
                          !entry.matchResult.found ? '#f8d7da' : '#fff';

                        return (
                          <tr key={i} style={{ background: rowColor }}>
                            <td>{(agencyPage - 1) * perPage + i + 1}</td>
                            <td><strong>{entry.csvAgencyName}</strong></td>
                            <td>
                              {entry.confirmedInmobiliariaId && !entry.createNew ? (
                                <span className="text-success">
                                  {inmobiliarias.find(i => i.id === entry.confirmedInmobiliariaId)?.nombre || entry.matchResult.matched}
                                </span>
                              ) : entry.createNew ? (
                                <span className="text-warning"><em>Nueva: {entry.csvAgencyName}</em></span>
                              ) : (
                                entry.matchResult.matched || <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${entry.matchResult.confidence >= 80 ? 'bg-success' : entry.matchResult.confidence >= 70 ? 'bg-warning' : 'bg-danger'}`}>
                                {Math.round(entry.matchResult.confidence)}%
                              </span>
                            </td>
                            <td><small>{entry.matchResult.method}</small></td>
                            <td>{entry.memberCount}</td>
                            <td>
                              {entry.createNew && <span className="badge bg-warning">Crear nueva</span>}
                              {entry.confirmedInmobiliariaId && !entry.createNew && <span className="badge bg-success">Confirmada</span>}
                              {!isResolved && <span className="badge bg-secondary">Pendiente</span>}
                            </td>
                            <td>
                              <div className="d-flex gap-1 align-items-center">
                                {/* Confirm match button */}
                                {entry.matchResult.found && entry.matchResult.id && !entry.confirmedInmobiliariaId && (
                                  <button
                                    className="btn btn-outline-success btn-sm py-0"
                                    title="Confirmar match"
                                    onClick={() => confirmAgency(globalIdx, entry.matchResult.id!)}
                                  >
                                    <i className="fas fa-check"></i>
                                  </button>
                                )}
                                {/* Create new */}
                                {!entry.createNew && !entry.confirmedInmobiliariaId && (
                                  <button
                                    className="btn btn-outline-warning btn-sm py-0"
                                    title="Crear nueva agencia"
                                    onClick={() => markCreateNew(globalIdx)}
                                  >
                                    <i className="fas fa-plus"></i>
                                  </button>
                                )}
                                {/* Manual select with search */}
                                {!entry.confirmedInmobiliariaId && !entry.createNew && (
                                  <>
                                    <input
                                      className="form-control form-control-sm"
                                      style={{ maxWidth: 200, fontSize: '0.75rem' }}
                                      list={`inm-list-${globalIdx}`}
                                      placeholder="Buscar agencia..."
                                      onChange={e => {
                                        const selected = inmobiliarias.find(inm => inm.nombre === e.target.value);
                                        if (selected) {
                                          confirmAgency(globalIdx, selected.id);
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                    <datalist id={`inm-list-${globalIdx}`}>
                                      {inmobiliarias
                                        .sort((a, b) => a.nombre.localeCompare(b.nombre))
                                        .map(inm => (
                                          <option key={inm.id} value={inm.nombre} />
                                        ))
                                      }
                                    </datalist>
                                  </>
                                )}
                                {/* Reset */}
                                {isResolved && (
                                  <button
                                    className="btn btn-outline-secondary btn-sm py-0"
                                    title="Resetear"
                                    onClick={() => {
                                      setAgencyMatches(prev => {
                                        const updated = [...prev];
                                        updated[globalIdx] = { ...updated[globalIdx], confirmedInmobiliariaId: null, createNew: false };
                                        return updated;
                                      });
                                    }}
                                  >
                                    <i className="fas fa-undo"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {agencyTotalPages > 1 && (
                  <nav>
                    <ul className="pagination pagination-sm justify-content-center">
                      {Array.from({ length: agencyTotalPages }, (_, i) => (
                        <li key={i} className={`page-item ${agencyPage === i + 1 ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setAgencyPage(i + 1)}>{i + 1}</button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}

                {/* Proceed button */}
                <div className="mt-3 d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-primary"
                    onClick={proceedToMembers}
                    disabled={loading || agencyStats.resolved < agencyStats.total}
                  >
                    {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Procesando...</> :
                      `Proceder a Miembros (${agencyStats.resolved}/${agencyStats.total} resueltas)`}
                  </button>
                  {agencyStats.resolved < agencyStats.total && (
                    <small className="text-danger">Resuelve todas las agencias antes de continuar</small>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ==================== STEP: MEMBER REVIEW ==================== */}
          {(step === 'member-review' || step === 'applying') && (
            <div className="card">
              <div className="card-body">
                <h5>Asignación de Agencias y Teléfonos a Miembros</h5>

                {/* Stats */}
                <div className="row g-2 mb-3">
                  <div className="col-3 col-md-2">
                    <div className="card border-0 bg-light text-center p-2">
                      <h4 className="mb-0">{memberStats.total}</h4>
                      <small className="text-muted">Total</small>
                    </div>
                  </div>
                  <div className="col-3 col-md-2">
                    <div className="card border-0 text-center p-2" style={{ background: '#d4edda' }}>
                      <h4 className="mb-0 text-success">{memberStats.correct}</h4>
                      <small>Correctos</small>
                    </div>
                  </div>
                  <div className="col-3 col-md-2">
                    <div className="card border-0 text-center p-2" style={{ background: '#fff3cd' }}>
                      <h4 className="mb-0 text-warning">{memberStats.needsUpdate}</h4>
                      <small>Actualizar</small>
                    </div>
                  </div>
                  <div className="col-3 col-md-2">
                    <div className="card border-0 text-center p-2" style={{ background: '#f8d7da' }}>
                      <h4 className="mb-0 text-danger">{memberStats.notFound}</h4>
                      <small>No encontrado</small>
                    </div>
                  </div>
                  <div className="col-3 col-md-2">
                    <div className="card border-0 text-center p-2" style={{ background: '#cfe2ff' }}>
                      <h4 className="mb-0" style={{ color: '#0d6efd' }}>{memberStats.phoneUpdates}</h4>
                      <small>Tel. update</small>
                    </div>
                  </div>
                  {memberStats.noAgency > 0 && (
                    <div className="col-3 col-md-2">
                      <div className="card border-0 text-center p-2" style={{ background: '#e2e3e5' }}>
                        <h4 className="mb-0">{memberStats.noAgency}</h4>
                        <small>Sin agencia</small>
                      </div>
                    </div>
                  )}
                </div>

                {/* Filters */}
                <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                  <div className="btn-group btn-group-sm">
                    {[
                      { key: 'all', label: `Todos (${memberStats.total})` },
                      { key: 'needs_update', label: `Actualizar (${memberStats.needsUpdate})` },
                      { key: 'phone_update', label: `Tel. (${memberStats.phoneUpdates})` },
                      { key: 'correct', label: `Correctos (${memberStats.correct})` },
                      { key: 'not_found', label: `No encontrado (${memberStats.notFound})` },
                    ].map(f => (
                      <button
                        key={f.key}
                        className={`btn ${filterMember === f.key ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => { setFilterMember(f.key as any); setMemberPage(1); }}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ maxWidth: 250 }}
                    placeholder="Buscar miembro..."
                    value={searchMember}
                    onChange={e => { setSearchMember(e.target.value); setMemberPage(1); }}
                  />
                </div>

                {/* Member table */}
                <div className="table-responsive">
                  <table aria-label="Tabla de Reconciliar Agencias Csv" className="table table-sm table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Nombre</th>
                        <th scope="col">Correo</th>
                        <th scope="col">Tipo</th>
                        <th scope="col">Agencia CSV</th>
                        <th scope="col">Agencia Actual</th>
                        <th scope="col">Tel CSV</th>
                        <th scope="col">Tel Actual</th>
                        <th scope="col">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedMembers.map((a, i) => {
                        const rowColor = !a.member ? '#f8d7da' :
                          a.needsUpdate ? '#fff3cd' :
                          a.expectedInmobiliariaId ? '#d4edda' : '#fff';

                        return (
                          <tr key={i} style={{ background: rowColor }}>
                            <td>{(memberPage - 1) * perPage + i + 1}</td>
                            <td>
                              <strong>{a.csvRow.nombre}</strong>
                              {a.member && a.matchConfidence < 100 && (
                                <small className="d-block text-muted">→ {a.member.nombre} ({Math.round(a.matchConfidence)}%)</small>
                              )}
                            </td>
                            <td><small>{a.csvRow.correo}</small></td>
                            <td>
                              <span className={`badge ${a.csvRow.tipo === 'ASOCIADO' ? 'bg-success' : 'bg-primary'}`} style={{ fontSize: '0.65rem' }}>
                                {a.csvRow.tipo}
                              </span>
                            </td>
                            <td><small>{a.expectedAgencyName}</small></td>
                            <td>
                              <small>{a.currentAgencyName || <span className="text-danger">Sin agencia</span>}</small>
                            </td>
                            <td>
                              <small className={a.needsPhoneUpdate ? 'text-primary fw-bold' : ''}>
                                {a.csvRow.telefono || <span className="text-muted">-</span>}
                              </small>
                            </td>
                            <td>
                              <small className={a.needsPhoneUpdate ? 'text-danger' : ''}>
                                {a.currentPhone || <span className="text-muted">-</span>}
                              </small>
                            </td>
                            <td>
                              {!a.member && <span className="badge bg-danger">No encontrado</span>}
                              {a.member && a.needsUpdate && (
                                <span className="badge bg-warning text-dark">
                                  {a.needsPhoneUpdate && a.currentInmobiliariaId !== a.expectedInmobiliariaId ? 'Agencia+Tel' :
                                   a.needsPhoneUpdate ? 'Teléfono' : 'Agencia'}
                                </span>
                              )}
                              {a.member && !a.needsUpdate && a.expectedInmobiliariaId && <span className="badge bg-success">OK</span>}
                              {a.member && !a.expectedInmobiliariaId && <span className="badge bg-secondary">Sin agencia</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {memberTotalPages > 1 && (
                  <nav>
                    <ul className="pagination pagination-sm justify-content-center">
                      {Array.from({ length: memberTotalPages }, (_, i) => (
                        <li key={i} className={`page-item ${memberPage === i + 1 ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setMemberPage(i + 1)}>{i + 1}</button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}

                {/* Apply button */}
                {step === 'member-review' && (
                  <div className="mt-3 d-flex gap-2 flex-wrap">
                    <button
                      className="btn btn-danger"
                      onClick={aplicarCambios}
                      disabled={loading || memberStats.needsUpdate === 0}
                    >
                      <i className="fas fa-save me-2"></i>
                      Aplicar {memberStats.needsUpdate} cambios
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={async () => {
                        setLoading(true);
                        addProgress('Propagando miembro_oficial a inmobiliarias y propiedades...');
                        try {
                          const r = await propagarMiembroOficial((msg) => addProgress(msg));
                          addProgress(`Propagación completada: ${r.inmobiliariasOficiales} inmobiliarias oficiales, ${r.marcadasOficial} props oficiales`);
                          setStep('done');
                        } catch (err: any) {
                          addProgress(`ERROR: ${err.message}`);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                    >
                      <i className="fas fa-sync me-2"></i>
                      Propagar a agencias públicas
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setStep('agency-review')}
                    >
                      Volver a Agencias
                    </button>
                  </div>
                )}
                {step === 'applying' && (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary"></div>
                    <p className="mt-2">Aplicando cambios...</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================== STEP: DONE ==================== */}
          {step === 'done' && (
            <div className="card">
              <div className="card-body text-center">
                <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                <h4>Reconciliación completada</h4>
                <p className="text-muted">
                  Se actualizaron las agencias de {memberStats.needsUpdate} miembros.
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <a href="/admin-lista-oficial" className="btn btn-primary">
                    Ver Lista Oficial
                  </a>
                  <a href="/admin-inm" className="btn btn-outline-secondary">
                    Ver Agencias
                  </a>
                  <a href="/admin-dashboard" className="btn btn-outline-secondary">
                    Dashboard
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* ==================== PROGRESS LOG ==================== */}
          {progress.length > 0 && (
            <div className="card mt-3">
              <div className="card-body">
                <h6><i className="fas fa-terminal me-2"></i>Log</h6>
                <div style={{ maxHeight: 200, overflow: 'auto', fontSize: '0.8rem', fontFamily: 'monospace', background: '#1e1e1e', color: '#d4d4d4', padding: 12, borderRadius: 6 }}>
                  {progress.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
